import type { EstimateHistoryPoint } from "../types";
import type { MarketBinding, MarketFreshness, PricingMethod, ResolvedMarket } from "../../market-data/types";

export type StudioStage = "research" | "synthesis" | "compose" | "review" | "publish";
export type ReportTemplate = "standard" | "data_heavy" | "event_driven" | "scenario";
export type PublicationMateriality = "editorial" | "minor" | "material" | "major";
export type ResolutionOutcome = "yes" | "no" | "invalid" | "other";
export type PublicationOutputStatus = "updated" | "created" | "unchanged" | "not_created" | "local_preview";

export type DriverDirection = "raises" | "lowers" | "mixed" | "neutral";
export type Importance = "low" | "medium" | "high" | "critical";
export type ConfidenceLevel = "low" | "medium" | "high";
export type DriverStatus = "active" | "watch" | "resolved";
export type Visibility = "visible" | "hidden";
export type PreferredDisplay = "auto" | "line_chart" | "bar_chart" | "table" | "timeline" | "scenario" | "compact" | "prose";
export type EditorialRole = "context" | "primary_driver" | "primary_evidence" | "supporting_evidence" | "mispricing" | "counterargument" | "uncertainty" | "next_watch" | "audit";
export type VisualWeight = "hero" | "featured" | "standard" | "supporting" | "audit";
export type MarketSourceMode = "polymarket" | "kalshi" | "ftm_internal";
export type ResolutionReview = "pending" | "reviewed";

export interface ExternalMarketConnection {
  sourceMode: "polymarket" | "kalshi";
  binding: MarketBinding;
  sourceUrl: string;
  importedQuestion: string;
  importedCloseTime: string | null;
  importedResolutionCriteria: string;
  importedStatus: "open" | "closed" | "resolved" | "archived";
  resolutionReview: ResolutionReview;
  connectedAt: string;
}

export interface MarketDataPublicationSnapshot {
  sourceMode: "polymarket" | "kalshi";
  binding: MarketBinding;
  sourceUrl: string;
  probability: number;
  observedAt: string | null;
  retrievedAt: string;
  pricingMethod: PricingMethod;
  freshness: MarketFreshness;
  providerStatus: string;
}

export interface MarketContract {
  title: string;
  slug: string;
  category: string;
  platform: string;
  marketUrl: string;
  currentMarketProbability: number | null;
  marketPriceUpdatedAt: string | null;
  resolutionCriteria: string;
  resolutionDate: string | null;
  marketStatus: "open" | "closed" | "resolved" | "archived" | "draft";
  resolvedAt?: string | null;
  outcome?: ResolutionOutcome | null;
  sourceMode?: MarketSourceMode;
  externalConnection?: ExternalMarketConnection | null;
}

export interface Baseline {
  previousFtmEstimate: number | null;
  baselineProbability: number | null;
  baselineType: "base_rate" | "previous_estimate" | "market" | "none";
  baselineSummary: string;
}

export interface Driver {
  id: string;
  name: string;
  description: string;
  direction: DriverDirection;
  importance: Importance;
  confidence: ConfidenceLevel;
  currentAssessment: string;
  status: DriverStatus;
  linkedResearchModuleIds: string[];
  updatedAt: string;
}

export interface PresentationMetadata {
  role: EditorialRole;
  importanceScore: number;
  takeaway: string;
  headline: string;
  preferredDisplay: PreferredDisplay;
  visibility: Visibility;
  featured: boolean;
  pinned: boolean;
  locked: boolean;
}

export interface ResearchModuleBase {
  id: string;
  title: string;
  description?: string;
  linkedDriverIds: string[];
  presentation: PresentationMetadata;
  updatedAt: string;
}

export interface EvidenceModule extends ResearchModuleBase {
  type: "evidence";
  summary: string;
  sourceName: string;
  sourceUrl?: string;
  publishedAt?: string | null;
  capturedAt: string;
  reliability?: ConfidenceLevel;
  freshness?: string;
}

export interface DatasetTimePoint {
  date: string;
  value: number;
  label?: string;
}

export interface DatasetCategoryPoint {
  label: string;
  value: number;
}

export interface DatasetModule extends ResearchModuleBase {
  type: "dataset";
  unit?: string;
  data: DatasetTimePoint[] | DatasetCategoryPoint[];
  sourceName: string;
  sourceUrl?: string;
}

export interface ModelModule extends ResearchModuleBase {
  type: "model";
  output: string;
  inputs: string[];
  assumptions: string[];
  modelConfidence: ConfidenceLevel;
  sourceName?: string;
  sourceUrl?: string;
}

export interface ScenarioOutcome {
  label: string;
  probability: number | null;
  description: string;
}

export interface ScenarioModule extends ResearchModuleBase {
  type: "scenario";
  outcomes: ScenarioOutcome[];
}

export interface TimelineEvent {
  date: string;
  title: string;
  summary: string;
  source?: string;
}

export interface TimelineModule extends ResearchModuleBase {
  type: "timeline";
  events: TimelineEvent[];
}

export interface SignalModule extends ResearchModuleBase {
  type: "signal";
  signalType: string;
  assessment: string;
  strength: ConfidenceLevel;
}

export interface AnalysisNoteModule extends ResearchModuleBase {
  type: "analysis_note";
  body: string;
}

export interface CustomModule extends ResearchModuleBase {
  type: "custom";
  body: string;
}

export type ResearchModule = EvidenceModule | DatasetModule | ModelModule | ScenarioModule | TimelineModule | SignalModule | AnalysisNoteModule | CustomModule;

export interface Synthesis {
  ftmEstimate: number | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  estimateConfidence: ConfidenceLevel | null;
  coreThesis: string;
  divergenceThesis: string;
  keyUncertainty: string;
  countercase: string;
  changeMindTriggers: string[];
}

export type LayoutComponent = "report_hero" | "estimate_hero" | "probability_history" | "core_thesis" | "driver_stack" | "featured_chart" | "supporting_chart" | "model_output" | "scenario_block" | "timeline" | "evidence_strip" | "divergence_thesis" | "countercase" | "watch_items" | "revision_sources";

export interface ReportBlock {
  id: string;
  component: LayoutComponent;
  sourceRef?: string;
  role: EditorialRole;
  slot: string;
  visualWeight: VisualWeight;
  headline: string;
  takeaway: string;
  preferredDisplay: PreferredDisplay;
  order: number;
  pinned: boolean;
  locked: boolean;
  visibility: Visibility;
  required?: boolean;
  systemOwned?: boolean;
}

export interface ReportLayoutSpec {
  reportId: string;
  template: ReportTemplate;
  blocks: ReportBlock[];
}

export interface StudioReport {
  reportId: string;
  createdAt: string;
  updatedAt: string;
  status: "draft" | "needs_review" | "reviewed" | "published";
  market: MarketContract;
  baseline: Baseline;
  drivers: Driver[];
  researchModules: ResearchModule[];
  synthesis: Synthesis;
  probabilityHistory?: EstimateHistoryPoint[];
  isDemo?: boolean;
  layout: ReportLayoutSpec;
}

export interface ResearchPackage {
  market?: Partial<MarketContract>;
  baseline?: Partial<Baseline>;
  drivers?: Driver[];
  researchModules?: ResearchModule[];
  synthesis?: Partial<Synthesis>;
}

export type ValidationSeverity = "BLOCKER" | "WARNING" | "INFO";
export type ValidationTarget = { stage: StudioStage; ref?: string };

export interface ValidationIssue {
  id: string;
  code: string;
  severity: ValidationSeverity;
  message: string;
  target: ValidationTarget;
}

export interface ValidationResult {
  issues: ValidationIssue[];
  blockers: ValidationIssue[];
  warnings: ValidationIssue[];
  info: ValidationIssue[];
  isPublishable: boolean;
  counts: { content: number; evidence: number; presentation: number; freshness: number };
}

export interface PublicationRevision {
  previousVersionId: string | null;
  previousEstimate: number | null;
  newEstimate: number | null;
  estimateDelta: number | null;
  changed: boolean;
}

export interface RecentIntelligenceEditorial {
  id: string;
  headline: string;
  summary: string;
  featured: boolean;
}

export interface PublicationOutputs {
  publicReport: PublicationOutputStatus;
  estimateHistory: PublicationOutputStatus;
  estimateRevision: PublicationOutputStatus;
  recentIntelligence: PublicationOutputStatus;
  marketCard: PublicationOutputStatus;
  archive: PublicationOutputStatus;
  manifest: PublicationOutputStatus;
}

export interface PublishedReportVersion {
  schemaVersion?: number;
  versionId: string;
  reportId: string;
  slug?: string;
  publishedAt: string;
  revisionNote: string;
  materiality?: PublicationMateriality;
  revision?: PublicationRevision;
  recentIntelligenceEditorial?: RecentIntelligenceEditorial | null;
  publicationOutputs?: PublicationOutputs;
  publicationOrigin?: "canonical" | "local_preview";
  marketSnapshot: MarketContract;
  marketDataSnapshot?: MarketDataPublicationSnapshot | null;
  researchSnapshot: { drivers: Driver[]; modules: ResearchModule[]; baseline: Baseline };
  synthesisSnapshot: Synthesis;
  layoutSnapshot: ReportLayoutSpec;
  report: StudioReport;
}

const DEFAULT_PRESENTATION: PresentationMetadata = {
  role: "context",
  importanceScore: 50,
  takeaway: "",
  headline: "",
  preferredDisplay: "auto",
  visibility: "visible",
  featured: false,
  pinned: false,
  locked: false,
};

export function createEmptyStudioReport(reportId: string, now = new Date().toISOString()): StudioReport {
  return {
    reportId,
    createdAt: now,
    updatedAt: now,
    status: "draft",
    market: {
      title: "",
      slug: "",
      category: "",
      platform: "",
      marketUrl: "",
      currentMarketProbability: null,
      marketPriceUpdatedAt: null,
      resolutionCriteria: "",
      resolutionDate: null,
      marketStatus: "draft",
      sourceMode: "ftm_internal",
      externalConnection: null,
    },
    baseline: { previousFtmEstimate: null, baselineProbability: null, baselineType: "none", baselineSummary: "" },
    drivers: [],
    researchModules: [],
    synthesis: { ftmEstimate: null, rangeLow: null, rangeHigh: null, estimateConfidence: null, coreThesis: "", divergenceThesis: "", keyUncertainty: "", countercase: "", changeMindTriggers: [] },
    layout: { reportId, template: "standard", blocks: [] },
  };
}

export function createPresentationMetadata(overrides: Partial<PresentationMetadata> = {}): PresentationMetadata {
  return { ...DEFAULT_PRESENTATION, ...overrides };
}

export function deriveDivergence(report: StudioReport, marketProbabilityOverride?: number | null): number | null {
  const market = marketProbabilityOverride === undefined ? report.market.currentMarketProbability : marketProbabilityOverride;
  const estimate = report.synthesis.ftmEstimate;
  if (!isProbability(market) || !isProbability(estimate)) return null;
  return estimate - market;
}

export function applyResearchPackage(report: StudioReport, packageInput: ResearchPackage): StudioReport {
  const next = structuredCloneSafe(report);
  next.market = { ...next.market, ...packageInput.market };
  next.baseline = { ...next.baseline, ...packageInput.baseline };
  if (packageInput.drivers) next.drivers = structuredCloneSafe(packageInput.drivers);
  if (packageInput.researchModules) next.researchModules = structuredCloneSafe(packageInput.researchModules);
  if (packageInput.synthesis) next.synthesis = { ...next.synthesis, ...packageInput.synthesis };
  next.status = "needs_review";
  next.updatedAt = new Date().toISOString();
  return next;
}

export function isProbability(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

export function linkResearchModule(report: StudioReport, module: ResearchModule, driverIds = report.drivers.map((driver) => driver.id)): void {
  const linkedDriverIds = [...new Set(driverIds.filter((id) => report.drivers.some((driver) => driver.id === id)))];
  const nextModule = structuredCloneSafe(module);
  nextModule.linkedDriverIds = linkedDriverIds;
  const existingIndex = report.researchModules.findIndex((item) => item.id === module.id);
  if (existingIndex >= 0) report.researchModules[existingIndex] = nextModule;
  else report.researchModules.push(nextModule);
  report.drivers.forEach((driver) => {
    driver.linkedResearchModuleIds = driver.linkedResearchModuleIds.filter((id) => id !== module.id);
    if (linkedDriverIds.includes(driver.id)) driver.linkedResearchModuleIds.push(module.id);
  });
}

export function unlinkResearchModule(report: StudioReport, moduleId: string, driverId: string): void {
  const module = report.researchModules.find((item) => item.id === moduleId);
  if (module) module.linkedDriverIds = module.linkedDriverIds.filter((id) => id !== driverId);
  const driver = report.drivers.find((item) => item.id === driverId);
  if (driver) driver.linkedResearchModuleIds = driver.linkedResearchModuleIds.filter((id) => id !== moduleId);
}

export function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
